import { FunnelButton } from '@/components/FunnelButton';
import { useMediaQuery } from '@/components/HomePageBanner/hooks/useMediaQuery';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { Eyebrow } from '../Eyebrow/Eybrow';
import styles from './HowItWorks.module.scss';

// How far down the viewport should the fadeout for the backgrounds happen
const TRANSITION_HEIGHT = 600;
// How should the transition take according to the scroll height
const FADE_DURATION_IN_PIXEL = 100;

const StopWatchSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
    <g clipPath="url(#clip0_7527_2598)">
      <path
        d="M9.73427 5.7799L8.54599 7.2632C8.60896 7.71008 8.37841 8.1468 7.9742 8.3479C7.56947 8.54849 7.08248 8.46825 6.76357 8.14832C6.44518 7.82841 6.36697 7.34091 6.56959 6.9377C6.7722 6.53398 7.21045 6.30547 7.65682 6.36996L9.14012 5.18168C9.30821 5.04609 9.55145 5.0593 9.70429 5.21215C9.85714 5.3645 9.86983 5.60774 9.73425 5.77582L9.73427 5.7799ZM5.44427 9.44733C5.44427 9.15078 5.20407 8.91007 4.90751 8.91007H2.64928C2.35272 8.91007 2.11252 9.15078 2.11252 9.44733C2.11252 9.74338 2.35272 9.98409 2.64928 9.98409H4.90595C5.04865 9.98409 5.18524 9.92722 5.2858 9.82667C5.38634 9.72613 5.44321 9.58953 5.44321 9.44733H5.44427ZM12.2317 3.68157C12.3449 3.5729 12.4191 3.4297 12.4424 3.27482C12.4688 3.06712 12.3983 2.85943 12.25 2.71166C12.1022 2.56338 11.8945 2.49279 11.6868 2.51919C11.5319 2.54255 11.3887 2.61669 11.2801 2.72993L10.7235 3.28649C9.98972 2.71215 9.11423 2.34551 8.19006 2.22566V1.71989H8.67503C9.02898 1.72598 9.33062 1.46497 9.3758 1.11406C9.39409 0.925665 9.33264 0.737776 9.20619 0.596596C9.07975 0.455936 8.89947 0.374686 8.71006 0.373152H6.35836C6.00441 0.367058 5.70277 0.627562 5.65758 0.978978C5.6388 1.16839 5.70126 1.35678 5.82872 1.49796C5.95668 1.63913 6.13797 1.71987 6.32841 1.71987H6.84841V2.22413C5.97701 2.33991 5.14827 2.67354 4.43938 3.19405C4.15855 3.41901 4.10473 3.82577 4.31801 4.11624C4.53078 4.40621 4.9345 4.47729 5.23411 4.27671C5.99431 3.7171 6.93321 3.45406 7.87428 3.53734C8.81475 3.62062 9.69272 4.04413 10.3438 4.72867C10.9943 5.4132 11.3726 6.31155 11.4076 7.25561C11.4432 8.19913 11.1324 9.12332 10.5347 9.85457C9.93702 10.5858 9.09301 11.0738 8.16116 11.2267C7.22932 11.3801 6.27317 11.1876 5.47328 10.6859C5.19043 10.5051 4.81719 10.5605 4.59933 10.8159C4.47492 10.9637 4.41957 11.1577 4.44851 11.3491C4.47694 11.54 4.58663 11.7096 4.74913 11.8143C5.80893 12.4896 7.07795 12.7568 8.3201 12.5658C9.53987 12.3728 10.6524 11.7553 11.4609 10.822C12.2699 9.88863 12.7228 8.69935 12.7401 7.46487C12.7568 6.30097 12.3805 5.16595 11.6716 4.24334L12.2317 3.68157ZM4.90594 6.15157C5.20249 6.15157 5.4432 5.91137 5.4432 5.61482C5.4432 5.31826 5.20249 5.07755 4.90594 5.07755H0.796767C0.500211 5.07755 0.26001 5.31826 0.26001 5.61482C0.26001 5.91137 0.500211 6.15157 0.796767 6.15157H4.90594ZM4.90594 6.99252H1.80674C1.51018 6.99252 1.26998 7.23323 1.26998 7.52978C1.26998 7.82583 1.51018 8.06654 1.80674 8.06654H4.90594C5.20249 8.06654 5.4432 7.82583 5.4432 7.52978C5.4432 7.23323 5.20249 6.99252 4.90594 6.99252Z"
        fill="#3B015F"
      />
    </g>
    <defs>
      <clipPath id="clip0_7527_2598">
        <rect width="13" height="13" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Linearly scaling function to determine opacity
 * When the position is after the "fadeHeight" the opacity is constant
 * When the position is after the height and not quite at the end, linearly scale opacity
 * When the position is before the sum of fadeHeight and distance, the opacity is zero
 */
const getFadingOpacity = ({
  currentPosition,
  fadeDistance,
  fadeHeight,
  startingOpacity,
}: {
  currentPosition: number;
  fadeHeight: number;
  fadeDistance: number;
  startingOpacity: number;
}) => {
  const opacity = startingOpacity + startingOpacity * ((currentPosition - fadeHeight) / fadeDistance);
  return currentPosition > fadeHeight ? startingOpacity : opacity;
};

/**
 * Slides the component along with the scroll height and locks it at some height
 */
const getSlidingPosition = ({
  currentPosition,
  lockedPosition,
  fadeHeight,
}: {
  currentPosition: number;
  lockedPosition: number;
  fadeHeight: number;
}) => {
  const calculatedPosition = currentPosition + lockedPosition - fadeHeight;
  const top = calculatedPosition < lockedPosition ? lockedPosition : calculatedPosition;
  return top;
};

export type HowItWorksGridItem = {
  title: string;
  body: ReactNode | string;
  imgSrc: string;
  imgAlt: string;
  pill: string;
};

export type HowItWorksContentProps = {
  eyebrow: string;
  heading: string;
  subtext: string;
  gridItems: HowItWorksGridItem[];
};

export interface HowItWorksProps {
  content: HowItWorksContentProps;
}

export const HowItWorks = ({ content }: HowItWorksProps) => {
  const { isMobile } = useMediaQuery();
  const observerRef = useRef<HTMLDivElement>(null);
  const [backgroundStyle, setBackgroundStyle] = useState<CSSProperties>({});

  const { eyebrow, heading, subtext, gridItems } = content;

  const handleScroll = () => {
    if (observerRef.current) {
      const position = observerRef.current.getBoundingClientRect();
      setBackgroundStyle({
        top: getSlidingPosition({
          currentPosition: position.y,
          // Pixel position where the numbers line up with the title before fading out
          lockedPosition: -200,
          fadeHeight: TRANSITION_HEIGHT,
        }),
        opacity: getFadingOpacity({
          currentPosition: position.y,
          fadeDistance: FADE_DURATION_IN_PIXEL,
          fadeHeight: TRANSITION_HEIGHT,
          // Opacity from designs
          startingOpacity: 0.12,
        }),
        zIndex: -1,
        transition: 'all 0.01s ease',
      });
    }
  };

  useEffect(() => {
    // Initialize the Intersection Observer
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    };

    if (observerRef.current) {
      const observer = new IntersectionObserver(entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          window.addEventListener('scroll', handleScroll);
        }
      }, options);

      observer.observe(observerRef.current);

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }
  });

  return (
    <div className={styles.fullWidthContainer}>
      <div className={styles.headerContainer}>
        <hgroup className={styles.header}>
          <div className={styles.eyebrow}>
            <Eyebrow>{eyebrow}</Eyebrow>
          </div>
          <h2 className={styles.title}>{heading}</h2>
        </hgroup>
        <div className={styles.subtitle}>{subtext}</div>
      </div>
      <div className={styles.proofPointContainer} ref={observerRef}>
        {gridItems.map(({ imgSrc, imgAlt, title, body, pill }, index) => (
          <div className={styles.proofPointFullWidth} key={title}>
            <div className={styles.proofPoint}>
              <img
                className={styles.proofPointImage}
                src={imgSrc}
                alt={imgAlt}
                width={202}
                height={202}
                loading="lazy"
              />
              <h3 className={styles.proofPointTitle}>{title}</h3>
              <p className={styles.proofPointBody}>{body}</p>
              {pill && (
                <div className={styles.proofPointPill}>
                  <StopWatchSVG />
                  {pill}
                </div>
              )}
            </div>
            {!isMobile && (
              <div className={styles.proofPointBackground} style={backgroundStyle}>
                {index + 1}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.ctaContainer}>
        <FunnelButton>Get started</FunnelButton>
        <button className={styles.button} type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
            <path d="M16.6667 1.66699H3.33341C2.41675 1.66699 1.66675 2.41699 1.66675 3.33366V18.3337L5.00008 15.0003H16.6667C17.5834 15.0003 18.3334 14.2503 18.3334 13.3337V3.33366C18.3334 2.41699 17.5834 1.66699 16.6667 1.66699Z" />
          </svg>
          Talk to us
        </button>
      </div>
    </div>
  );
};
